import { FormCheckboxCard } from "@/shared/ui/FormCheckboxCard";
import {
  Button,
  FormInput,
  FormNumber,
  FormRoot,
  FormSelect,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { PlusIcon, TrashIcon } from "lucide-react";
import { z } from "zod";
import type {
  CreateInstallmentPlanTemplateDTO,
  UpdateInstallmentPlanTemplateDTO,
} from "../../domain/contracts";

const RowSchema = z.object({
  installmentNo: z.number().int().positive(),
  label: z.string().max(200),
  labelEn: z.string().max(200),
  percent: z.number().int().min(1).max(100),
  dueOffsetDays: z.number().int().min(0),
});

const InstallmentPlanTemplateFormSchema = z
  .object({
    name: z.string().min(1, "ຕ້ອງປ້ອນຊື່ແມ່ແບບ"),
    status: z.enum(["active", "inactive"]),
    sortOrder: z.number().int(),
    isDefault: z.boolean(),
    rows: z.array(RowSchema).min(1),
  })
  .superRefine((value, ctx) => {
    const sum = value.rows.reduce((acc, row) => acc + row.percent, 0);
    if (sum !== 100) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ຜົນລວມ % ຕ້ອງເທົ່າ 100",
        path: ["rows"],
      });
    }
  });

type InstallmentPlanTemplateFormValues = z.infer<
  typeof InstallmentPlanTemplateFormSchema
>;

type InstallmentPlanTemplateFormProps = {
  initialValues?: Partial<InstallmentPlanTemplateFormValues>;
  onSubmit: (
    values:
      | CreateInstallmentPlanTemplateDTO
      | UpdateInstallmentPlanTemplateDTO,
  ) => void;
  onCancel?: () => void;
  submitting?: boolean;
};

const DEFAULT_ROWS: InstallmentPlanTemplateFormValues["rows"] = [
  {
    installmentNo: 1,
    label: "ມັດຈຳ",
    labelEn: "Deposit",
    percent: 40,
    dueOffsetDays: 14,
  },
  {
    installmentNo: 2,
    label: "ງວດ 2",
    labelEn: "Installment 2",
    percent: 40,
    dueOffsetDays: 30,
  },
  {
    installmentNo: 3,
    label: "ປິດຍອດ",
    labelEn: "Final balance",
    percent: 20,
    dueOffsetDays: 60,
  },
];

export function InstallmentPlanTemplateForm({
  initialValues,
  onSubmit,
  onCancel,
  submitting,
}: InstallmentPlanTemplateFormProps) {
  const methods = RHF.useForm<InstallmentPlanTemplateFormValues>({
    resolver: zodResolver(InstallmentPlanTemplateFormSchema),
    defaultValues: {
      name: initialValues?.name ?? "",
      status: initialValues?.status ?? "active",
      sortOrder: initialValues?.sortOrder ?? 0,
      isDefault: initialValues?.isDefault ?? false,
      rows: initialValues?.rows?.length ? initialValues.rows : DEFAULT_ROWS,
    },
  });

  const { fields, append, remove } = RHF.useFieldArray({
    control: methods.control,
    name: "rows",
  });

  const watchedRows = methods.watch("rows");
  const percentSum = watchedRows.reduce((acc, row) => acc + (row.percent || 0), 0);

  const renumber = (
    rows: InstallmentPlanTemplateFormValues["rows"],
  ): InstallmentPlanTemplateFormValues["rows"] =>
    rows.map((row, index) => ({ ...row, installmentNo: index + 1 }));

  return (
    <FormRoot
      methods={methods}
      onSubmit={(vals) => {
        onSubmit({
          name: vals.name,
          status: vals.status,
          sortOrder: vals.sortOrder,
          isDefault: vals.isDefault,
          rows: renumber(vals.rows).map((row) => ({
            installmentNo: row.installmentNo,
            label: row.label.trim() ? row.label.trim() : null,
            labelEn: row.labelEn.trim() ? row.labelEn.trim() : null,
            percent: row.percent,
            dueOffsetDays: row.dueOffsetDays,
          })),
        });
      }}
      className="space-y-4 py-6"
    >
      <FormInput name="name" label="ຊື່ແມ່ແບບ" required />
      <div className="grid grid-cols-2 gap-4">
        <FormSelect
          name="status"
          label="ສະຖານະ"
          options={[
            { value: "active", label: "ໃຊ້ງານ" },
            { value: "inactive", label: "ບໍ່ໃຊ້ງານ" },
          ]}
        />
        <FormNumber name="sortOrder" label="ລຳດັບການສະແດງ" min={0} />
      </div>
      <FormCheckboxCard
        name="isDefault"
        label="ຕັ້ງເປັນແມ່ແບບເລີ່ມຕົ້ນ"
        description="ໃຊ້ອັດຕະໂນມັດເມື່ອອອກໃບເກັບເງິນ (ຖ້າຍັງບໍ່ມີແຜນງວດ)"
      />

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-sm">ງວດຊຳລະ</p>
            <p className="text-muted-foreground text-xs">
              ຈຳນວນມື້ = ຫຼັງອອກໃບເກັບເງິນ (ງວດທຳອິດມັກເປັນ 0)
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              append({
                installmentNo: fields.length + 1,
                label: `ງວດ ${fields.length + 1}`,
                labelEn: `Installment ${fields.length + 1}`,
                percent: 1,
                dueOffsetDays: fields.length * 30,
              })
            }
          >
            <PlusIcon className="size-4" /> ເພີ່ມງວດ
          </Button>
        </div>

        <div className="space-y-2">
          <div className="grid grid-cols-[48px_1fr_1fr_88px_100px_40px] gap-2 px-1 text-muted-foreground text-xs">
            <span>#</span>
            <span>ປ້າຍ (ລາວ)</span>
            <span>Label (EN)</span>
            <span>%</span>
            <span>ມື້ຫຼັງອອກ INV</span>
            <span />
          </div>
          {fields.map((field, index) => (
            <div
              key={field.id}
              className="grid grid-cols-[48px_1fr_1fr_88px_100px_40px] items-start gap-2"
            >
              <div className="flex h-9 items-center justify-center rounded-md border bg-muted/40 text-sm">
                {index + 1}
              </div>
              <FormInput name={`rows.${index}.label`} label="" />
              <FormInput name={`rows.${index}.labelEn`} label="" />
              <FormNumber name={`rows.${index}.percent`} label="" min={1} max={100} />
              <FormNumber
                name={`rows.${index}.dueOffsetDays`}
                label=""
                min={0}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="mt-0.5"
                disabled={fields.length <= 1}
                onClick={() => remove(index)}
              >
                <TrashIcon className="size-4" />
              </Button>
            </div>
          ))}
        </div>

        <p
          className={
            percentSum === 100
              ? "text-muted-foreground text-sm"
              : "font-medium text-destructive text-sm"
          }
        >
          ຜົນລວມ %: {percentSum}
          {percentSum === 100 ? " ✓" : " (ຕ້ອງເທົ່າ 100)"}
        </p>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            ຍົກເລີກ
          </Button>
        )}
        <Button type="submit" disabled={submitting || percentSum !== 100}>
          {submitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
        </Button>
      </div>
    </FormRoot>
  );
}
